export interface CoeffParams { phi: number; delta: number; beta: number; psi: number; theta: number }
export interface ActiveCoeff { K: number; branch: "a" | "b" }

const sin2 = (x: number): number => Math.sin(x) ** 2;

/**
 * TBDY 2018 Denk. 16.24 — toplam (statik+dinamik) aktif toprak basıncı katsayısı.
 * θ = 0 ile klasik Coulomb'a indirgenir. ψ: duvar arka yüzünün yatayla açısı (düşey = π/2).
 */
export function activeCoefficient(p: CoeffParams): ActiveCoeff {
  const { phi, delta, beta, psi, theta } = p;
  const num = sin2(psi + phi - theta);
  const sPsiTD = Math.sin(psi - theta - delta);
  const d1 = Math.cos(theta) * sin2(psi) * sPsiTD;
  if (!(d1 > 0)) throw new Error("Duvar arka yüzü çok yatık: Coulomb kayma kaması oluşmuyor (ψ − θ − δ ≤ 0). Arka yüzü dikleştirin, δ'yı küçültün veya kesiti gözden geçirin.");
  if (beta <= phi - theta + 1e-12) {
    const inner = (Math.sin(phi + delta) * Math.sin(phi - beta - theta)) / (sPsiTD * Math.sin(psi + beta));
    const K = num / (d1 * (1 + Math.sqrt(Math.max(0, inner))) ** 2);
    return { K, branch: "a" };
  }
  return { K: num / d1, branch: "b" };
}

/** TBDY 2018 Denk. 16.25 — toplam pasif basınç katsayısı (duvar-zemin sürtünmesi yok). */
export function passiveCoefficient(p: Omit<CoeffParams, "delta">): number {
  const { phi, beta, psi, theta } = p;
  const num = sin2(psi + phi - theta);
  const s = Math.sin(psi + theta);
  const inner = (Math.sin(phi) * Math.sin(phi + beta - theta)) / (s * Math.sin(psi + beta));
  const root = Math.sqrt(Math.max(0, inner));
  const denom = Math.cos(theta) * sin2(psi) * s * (1 - root) ** 2;
  if (!(denom > 0)) throw new Error("Pasif basınç katsayısı tanımsız");
  return num / denom;
}

export const rankineActive = (phi: number): number => Math.tan(Math.PI / 4 - phi / 2) ** 2;
